import * as React from 'react';
import { useContext, useEffect, useRef, useState } from 'react';
import styled, { ThemeContext } from 'styled-components';
import type { ThemeType } from '../theme/glueTheme';
import { TopBar } from '../NavigationBar/TopBar';
import { KeyboardShortcutIcon } from '../base/KeyboardShortcutIcon';
import { ShortcutsSheet } from './ShortcutSheet/ShortcutsSheet';
import { ShortcutHint } from './ShortcutSheet/ShortcutHint';

// Helps the user move the cursor between the button and the sheet without losing it
const HoverGapBridger = styled.div`
    display: none;
    position: absolute;
    z-index: 1;
    top: 20px;
    right: 0;
    width: 80px;
    height: 50px;
`;

const ShortcutsButton = styled.button`
    position: relative;
    display: flex;
    background: ${props => props.theme.backgroundColor};
    border-radius: 8px;
    border: 1px solid ${props => props.theme.subtleAccentColor};
    padding: 2px;
    outline: none;
    transition: background-color 0.2s ease;
    margin-right: 8px;

    &:hover {
        background: ${props => props.theme.subtleAccentColor};
        cursor: pointer;
    }

    &:hover ${ShortcutsSheet} {
        display: flex;
    }
    &:hover ${HoverGapBridger} {
        display: block;
    }
`;

const TopBarButton = styled.button<{ $isActive?: boolean }>`
    display: flex;
    align-items: center;
    gap: 6px;
    background: ${props => (props.$isActive ? props.theme.subtleAccentColor : props.theme.backgroundColor)};
    border-radius: 8px;
    border: 1px solid ${props => props.theme.subtleAccentColor};
    padding: 4px 10px;
    outline: none;
    transition: background-color 0.2s ease;
    margin-right: 8px;
    color: ${props => props.theme.textColor};
    font-size: 13px;
    font-weight: 500;

    &:hover {
        background: ${props => props.theme.subtleAccentColor};
        cursor: pointer;
    }
`;

interface ClickToEditNameProps {
    value: string;
    onChange: (value: string) => void;
    className?: string;
}

const ClickToEditName: React.FC<ClickToEditNameProps> = ({ value, onChange, className }) => {
    const [isEditing, setIsEditing] = useState(false);
    const [editValue, setEditValue] = useState(value);
    const inputRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        if (isEditing && inputRef.current) {
            inputRef.current.select();
        }
    }, [isEditing]);

    useEffect(() => {
        if (!isEditing) {
            setEditValue(value);
        }
    }, [value, isEditing]);

    const handleSave = () => {
        setIsEditing(false);
        if (editValue.trim() && editValue !== value) {
            onChange(editValue.trim());
        } else {
            setEditValue(value);
        }
    };

    if (isEditing) {
        return (
            <input
                ref={inputRef}
                value={editValue}
                onChange={event => setEditValue(event.target.value)}
                onBlur={handleSave}
                onKeyDown={event => {
                    if (event.key === 'Enter') handleSave();
                    if (event.key === 'Escape') {
                        setEditValue(value);
                        setIsEditing(false);
                    }
                }}
                className={`bg-transparent border-none outline-none font-bold text-sm text-current ${className ?? ''}`}
                style={{ width: `${Math.max(editValue.length, 3)}ch` }}
            />
        );
    }

    return (
        <button
            onClick={() => setIsEditing(true)}
            className={`font-bold text-sm text-current bg-transparent border-none cursor-pointer rounded px-1 py-0.5 truncate max-w-[400px] transition-colors hover:bg-black/5 dark:hover:bg-white/10 ${className ?? ''}`}
        >
            {value || 'Untitled'}
        </button>
    );
};

interface Props {
    graphName: string;
    className?: string;
    onGraphNameChange: (name: string) => void;
    onRunsClick?: () => void;
    isRunsPanelOpen?: boolean;
    onSecretsClick?: () => void;
    isSecretsPanelOpen?: boolean;
    onAIEditClick?: () => void;
    isAIPanelOpen?: boolean;
    functionName?: string;
    onFunctionNameChange?: (name: string) => void;
    onBackToMainGraph?: () => void;
}

export const GraphEditorTopBar: React.FC<Props> = ({
    graphName,
    onGraphNameChange,
    className = '',
    onRunsClick,
    isRunsPanelOpen,
    onSecretsClick,
    isSecretsPanelOpen,
    onAIEditClick,
    isAIPanelOpen,
    functionName,
    onFunctionNameChange,
    onBackToMainGraph,
}) => {
    const { primaryContrastColor } = useContext<ThemeType>(ThemeContext);

    let shortcutMetaKey = 'Ctrl';
    if (window.navigator.userAgent.includes('Mac')) {
        shortcutMetaKey = '⌘';
    }

    return (
        <TopBar
            className={className}
            renderLeftExtra={() => <ClickToEditName value={graphName} onChange={onGraphNameChange} />}
            renderCenter={
                functionName !== undefined && onFunctionNameChange && onBackToMainGraph
                    ? () => (
                          <div className="flex items-center gap-2">
                              <button
                                  onClick={onBackToMainGraph}
                                  className="text-sm font-medium text-neutral-500 hover:text-neutral-700 dark:text-neutral-400 dark:hover:text-neutral-200 transition-colors cursor-pointer"
                              >
                                  ← Back
                              </button>
                              <span className="text-neutral-300 dark:text-neutral-600 text-sm">›</span>
                              <ClickToEditName value={functionName} onChange={onFunctionNameChange} />
                          </div>
                      )
                    : undefined
            }
            renderRightSection={() => (
                <>
                    <ShortcutsButton>
                        <KeyboardShortcutIcon size={32} color={primaryContrastColor} />
                        <HoverGapBridger />
                        <ShortcutsSheet>
                            <ShortcutHint keyText={`Tab`} description="Open the block library" />
                            <ShortcutHint keyText={`${shortcutMetaKey}+K`} description="" />
                            <ShortcutHint
                                keyText={'F'}
                                description="Frame your view on the blocks you've selected. If you don't have any selected it will frame the whole workflow."
                            />
                            <ShortcutHint keyText={'A'} description="Select all blocks in the workflow." />
                            <ShortcutHint keyText={`R`} description="Organize the selected blocks" />
                            <ShortcutHint keyText={`${shortcutMetaKey}+C`} description="Copy the selected blocks into your clipboard" />
                            <ShortcutHint
                                keyText={`${shortcutMetaKey}+V`}
                                description="Paste previously copied blocks into the graph. You can also paste things like numbers or text."
                            />
                            <ShortcutHint keyText={'Escape'} description="Deselect all blocks." />
                            <ShortcutHint keyText={'Delete'} description="Delete selected blocks." />
                        </ShortcutsSheet>
                    </ShortcutsButton>
                    {onRunsClick && (
                        <TopBarButton onClick={onRunsClick} $isActive={isRunsPanelOpen}>
                            <svg
                                width="16"
                                height="16"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke={primaryContrastColor}
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                            >
                                <circle cx="12" cy="12" r="10" />
                                <polyline points="12 6 12 12 16 14" />
                            </svg>
                            Runs
                        </TopBarButton>
                    )}
                    {onAIEditClick && (
                        <TopBarButton onClick={onAIEditClick} $isActive={isAIPanelOpen}>
                            <svg
                                width="16"
                                height="16"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke={primaryContrastColor}
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                            >
                                <path d="M12 3l1.5 5.5L19 10l-5.5 1.5L12 17l-1.5-5.5L5 10l5.5-1.5Z" />
                                <path d="M19 3l.5 2L22 5.5 19.5 6 19 8l-.5-2L16 5.5 18.5 5Z" />
                            </svg>
                            AI Edit
                        </TopBarButton>
                    )}
                    {onSecretsClick && (
                        <TopBarButton onClick={onSecretsClick} $isActive={isSecretsPanelOpen}>
                            <svg
                                width="16"
                                height="16"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke={primaryContrastColor}
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                            >
                                <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                                <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                            </svg>
                            Secrets
                        </TopBarButton>
                    )}
                </>
            )}
        />
    );
};
